import React, { useState, useEffect, useRef } from 'react';
import api from '../../api';
import {
  Briefcase, Plus, Users, FileText, CheckCircle, XCircle, MessageSquare,
  Sparkles, Download, Layers, Calendar, MapPin, UploadCloud,
  Check, X, Search, Filter, RefreshCw, Loader2, ArrowLeft, Building2, Eye,
  ChevronDown, RotateCcw, ExternalLink, GitBranch, Terminal, Edit2, Trash2, Mail
} from 'lucide-react';
import './InternalJobsModule.css';
import CandidateRankingPanel from '../tpo/CandidateRankingPanel';
import AIPlacementRecommendationCard from '../AIPlacementRecommendationCard';
import FormattedResumeSummary from '../FormattedResumeSummary';
import { buildSkillPayload, mergeGeneratedScores, parseSkillNames, scoresFromStoredSkills, updateSkillScoreMap } from '../../utils/jobSkillScores';
import { useFeatures } from '../FeatureGate';

// ─── REUSABLE BATCH MULTI-SELECT DROPDOWN ───
const BatchMultiSelectDropdown = ({
  selectedBatches = [],
  onChange,
  availableBatches = [],
  placeholder = "Select eligible batches..."
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const getBatchKey = (b) => {
    if (!b) return '';
    if (typeof b === 'string') return b;
    return b.label || b.value || '';
  };

  const getBatchLabel = (b) => {
    if (!b) return '';
    if (typeof b === 'string') return b;
    return b.label || b.value || '';
  };

  const isAllExplicitlySelected = selectedBatches.some(s => {
    if (!s) return false;
    const str = String(s).trim().toLowerCase();
    return str === 'all' || str === 'all batches';
  });

  const allAvailableKeys = availableBatches.map(getBatchKey).filter(Boolean);

  const areAllBatchesIndividuallySelected =
    allAvailableKeys.length > 0 &&
    allAvailableKeys.every(k => selectedBatches.includes(k));

  const isAllSelected = isAllExplicitlySelected || areAllBatchesIndividuallySelected;

  const isBatchSelected = (b) => {
    if (isAllSelected) return true;
    const key = getBatchKey(b);
    const label = getBatchLabel(b);
    return selectedBatches.includes(key) || selectedBatches.includes(label);
  };

  const toggleAll = () => {
    if (isAllSelected) {
      onChange([]);
    } else {
      onChange(['All']);
    }
  };

  const toggleBatch = (b) => {
    const key = getBatchKey(b);
    if (!key) return;

    if (isAllSelected) {
      // If currently all are selected, clicking one batch unselects that specific batch,
      // leaving all other available batches selected.
      const remaining = allAvailableKeys.filter(k => k !== key);
      onChange(remaining);
      return;
    }

    const currentlySelected = isBatchSelected(b);
    let updated;
    if (currentlySelected) {
      // Uncheck it
      updated = selectedBatches.filter(s => {
        const sLower = String(s).trim().toLowerCase();
        if (sLower === 'all' || sLower === 'all batches') return false;
        const bLabel = getBatchLabel(b);
        return s !== key && s !== bLabel;
      });
    } else {
      // Check it
      const cleanCurrent = selectedBatches.filter(s => {
        const sLower = String(s).trim().toLowerCase();
        return sLower !== 'all' && sLower !== 'all batches';
      });
      updated = [...cleanCurrent, key];
      // If all available batches are now selected, cleanly simplify to ['All']
      if (allAvailableKeys.length > 0 && allAvailableKeys.every(k => updated.includes(k))) {
        updated = ['All'];
      }
    }
    onChange(updated);
  };

  const removeBatchTag = (e, batchTag) => {
    e.stopPropagation();
    if (batchTag === 'All' || isAllSelected) {
      onChange([]);
    } else {
      onChange(selectedBatches.filter(b => b !== batchTag));
    }
  };

  const filteredBatches = availableBatches.filter(b => {
    const term = searchTerm.toLowerCase();
    const label = (getBatchLabel(b) || '').toLowerCase();
    const val = (typeof b === 'object' && b.value ? String(b.value) : '').toLowerCase();
    return label.includes(term) || val.includes(term);
  });

  return (
    <div className="batch-multiselect-container" ref={dropdownRef}>
      {/* Trigger Button */}
      <div
        className={`batch-multiselect-trigger ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        tabIndex={0}
      >
        <div className="batch-multiselect-tags">
          {selectedBatches.length === 0 ? (
            <span className="batch-multiselect-placeholder">{placeholder}</span>
          ) : isAllSelected ? (
            <span className="batch-tag batch-tag-all">
              All Batches
              <span className="batch-tag-remove" onClick={(e) => removeBatchTag(e, 'All')}>×</span>
            </span>
          ) : (
            selectedBatches.map(b => (
              <span key={b} className="batch-tag">
                {b}
                <span className="batch-tag-remove" onClick={(e) => removeBatchTag(e, b)}>×</span>
              </span>
            ))
          )}
        </div>
        <div className="batch-multiselect-icons">
          {(isAllSelected || selectedBatches.length > 0) && (
            <span className="batch-count-badge">
              {isAllSelected ? (allAvailableKeys.length > 0 ? allAvailableKeys.length : 'All') : selectedBatches.length}
            </span>
          )}
          <ChevronDown size={18} className={`batch-chevron ${isOpen ? 'rotated' : ''}`} />
        </div>
      </div>

      {/* Floating Dropdown Menu */}
      {isOpen && (
        <div className="batch-multiselect-menu">
          {/* Search bar inside dropdown */}
          <div className="batch-multiselect-search-wrap">
            <Search size={14} className="batch-search-icon" />
            <input
              type="text"
              placeholder="Search batches..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="batch-multiselect-search-input"
              autoFocus
              onClick={e => e.stopPropagation()}
            />
            {searchTerm && (
              <button
                type="button"
                className="batch-clear-search"
                onClick={(e) => { e.stopPropagation(); setSearchTerm(''); }}
              >
                ×
              </button>
            )}
          </div>

          {/* Options List */}
          <div className="batch-multiselect-options">
            {/* "All Batches" Quick Option */}
            {(!searchTerm || 'all batches'.includes(searchTerm.toLowerCase())) && (
              <div
                className={`batch-option-item ${isAllSelected ? 'selected' : ''}`}
                onClick={() => toggleAll()}
              >
                <div className={`batch-custom-checkbox ${isAllSelected ? 'checked' : ''}`}>
                  {isAllSelected && <Check size={13} strokeWidth={3} />}
                </div>
                <div className="batch-option-label">
                  <strong>All Batches</strong>
                  <span className="batch-option-desc">Eligible for all batches in your university</span>
                </div>
              </div>
            )}

            {filteredBatches.map(b => {
              const key = getBatchKey(b);
              const isChecked = isBatchSelected(b);
              return (
                <div
                  key={key}
                  className={`batch-option-item ${isChecked ? 'selected' : ''}`}
                  onClick={() => toggleBatch(b)}
                >
                  <div className={`batch-custom-checkbox ${isChecked ? 'checked' : ''}`}>
                    {isChecked && <Check size={13} strokeWidth={3} />}
                  </div>
                  <div className="batch-option-label">
                    <span>{getBatchLabel(b)}</span>
                  </div>
                </div>
              );
            })}

            {filteredBatches.length === 0 && (
              <div className="batch-no-options">
                No matching batches found
              </div>
            )}
          </div>

          {/* Dropdown Footer Actions */}
          <div className="batch-multiselect-footer">
            <span className="batch-selection-summary">
              {isAllSelected
                ? `All batches selected (${allAvailableKeys.length || 'All'})`
                : `${selectedBatches.length} batch(es) selected`}
            </span>
            <div style={{ display: 'flex', gap: '8px' }}>
              {(isAllSelected || selectedBatches.length > 0) && (
                <button
                  type="button"
                  className="batch-clear-btn"
                  onClick={(e) => { e.stopPropagation(); onChange([]); }}
                >
                  Clear
                </button>
              )}
              <button
                type="button"
                className="batch-done-btn"
                onClick={(e) => { e.stopPropagation(); setIsOpen(false); }}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const POPULAR_LOCATIONS = [
  'On-Site / Hybrid',
  'On-Site',
  'Hybrid',
  'Remote',
  'Bengaluru',
  'Hyderabad',
  'Pune',
  'Delhi NCR / Gurgaon / Noida',
  'Mumbai',
  'Chennai',
  'Kolkata',
  'Ahmedabad',
  'Chandigarh',
  'Coimbatore',
  'Jaipur'
];

const InternalJobsModule = ({ showToast }) => {
  const { isSubEnabled } = useFeatures();
  const canManage = isSubEnabled('internal_jobs', 'internal_jobs_manage');
  const canCreate = isSubEnabled('internal_jobs', 'internal_jobs_create');
  const canEditDelete = isSubEnabled('internal_jobs', 'internal_jobs_edit_delete');
  const canDecide = isSubEnabled('internal_jobs', 'internal_jobs_decision');
  const canRemark = isSubEnabled('internal_jobs', 'internal_jobs_feedback');
  const canReport = isSubEnabled('internal_jobs', 'internal_jobs_reports');

  const [subTab, setSubTab] = useState('jobs'); // 'jobs' | 'candidates' | 'report'
  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [availableBatches, setAvailableBatches] = useState([]);

  // Drilldown to candidates of a specific job
  const [selectedJob, setSelectedJob] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [candidateSearch, setCandidateSearch] = useState('');
  const [candidateStatusFilter, setCandidateStatusFilter] = useState('All');
  const [selectedApplication, setSelectedApplication] = useState(null);

  // Overall Report state
  const [reportData, setReportData] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [exportingReport, setExportingReport] = useState(false);

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [creatingJob, setCreatingJob] = useState(false);
  const [generatingAiDesc, setGeneratingAiDesc] = useState(false);
  const [parsingJdFile, setParsingJdFile] = useState(false);
  // Requirement scores for the student Skill Gap graph: generated from the role and
  // JD, shown in the form, and editable before publishing.
  const [skillScores, setSkillScores] = useState({ must: {}, good: {} });
  const [scoringSkills, setScoringSkills] = useState(false);
  const [isCustomLocation, setIsCustomLocation] = useState(false);
  const [fileInputKey, setFileInputKey] = useState(0);

  // Create Job Form
  const [jobForm, setJobForm] = useState({
    title: '',
    company_name: '',
    description: '',
    responsibilities: '',
    requirements: '',
    must_have_skills: '',
    good_to_have_skills: '',
    salary_package: 'Industry Standard',
    location: 'On-Site / Hybrid',
    target_batches: ['All'],
    status: 'ACTIVE',
    jd_file: null,
    jd_file_name: ''
  });

  // Batch Mapping Modal
  const [batchModalJob, setBatchModalJob] = useState(null);
  const [selectedBatchesForJob, setSelectedBatchesForJob] = useState([]);
  const [savingBatches, setSavingBatches] = useState(false);

  // Remarks Modal
  const [remarksModalCandidate, setRemarksModalCandidate] = useState(null);
  const [remarksText, setRemarksText] = useState('');
  const [savingRemarks, setSavingRemarks] = useState(false);

  // Disapprove Candidate Modal (Mandatory Remarks)
  const [disapproveModalCandidate, setDisapproveModalCandidate] = useState(null);
  const [disapproveRemarks, setDisapproveRemarks] = useState('');
  const [savingDisapproval, setSavingDisapproval] = useState(false);

  useEffect(() => {
    if (canManage) {
      fetchJobs();
      fetchBatches();
    }
  }, [canManage]);

  const fetchJobs = async () => {
    setLoadingJobs(true);
    try {
      const res = await api.get('/tpo/internal-jobs');
      const data = res.data || [];
      setJobs(data);
      setSelectedJob(prev => prev ? (data.find(j => j.id === prev.id) || prev) : null);
    } catch (err) {
      console.error('Failed to fetch internal jobs', err);
      showToast('Failed to load internal jobs', 'error');
    } finally {
      setLoadingJobs(false);
    }
  };

  const fetchBatches = async () => {
    try {
      const res = await api.get('/tpo/batches');
      setAvailableBatches(res.data || []);
    } catch (err) {
      console.error('Failed to fetch batches', err);
    }
  };

  const fetchJobCandidates = async (job) => {
    setSelectedJob(job);
    setSelectedApplication(null);
    setLoadingCandidates(true);
    try {
      const res = await api.get(`/tpo/internal-jobs/${job.id}/candidates`);
      setCandidates(res.data?.candidates || []);
      setSubTab('candidates');
    } catch (err) {
      console.error('Failed to fetch candidates', err);
      showToast('Failed to load candidates', 'error');
    } finally {
      setLoadingCandidates(false);
    }
  };

  const fetchReport = async () => {
    setLoadingReport(true);
    try {
      const res = await api.get('/tpo/internal-jobs/report');
      setReportData(res.data || null);
    } catch (err) {
      console.error('Failed to fetch report', err);
      showToast('Failed to load report', 'error');
    } finally {
      setLoadingReport(false);
    }
  };

  const handleExportCsv = async () => {
    setExportingReport(true);
    try {
      const res = await api.get('/tpo/internal-jobs/report/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `campus_jobs_report_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      showToast('Report downloaded successfully!');
    } catch (err) {
      console.error('Export failed', err);
      showToast('Failed to export CSV report', 'error');
    } finally {
      setExportingReport(false);
    }
  };

  // AI Description Generator (Generates full role details, responsibilities, requirements & skills from Title)
  const handleGenerateAiDescription = async () => {
    if (!jobForm.title.trim()) {
      showToast('Please enter a Job Title first', 'error');
      return;
    }
    setGeneratingAiDesc(true);
    try {
      const res = await api.post('/tpo/internal-jobs/generate-description', {
        title: jobForm.title,
        company_name: jobForm.company_name
      });
      const data = res.data;

      const formatBulletList = (val) => {
        if (!val) return '';
        let lines = [];
        if (Array.isArray(val)) {
          lines = val;
        } else if (typeof val === 'string') {
          lines = val.split('\n');
        } else {
          return String(val);
        }
        return lines
          .map(it => {
            let clean = String(it).trim();
            if (!clean) return '';
            clean = clean.replace(/^([•\-\*]|\d+\.)\s*/, '');
            return `• ${clean}`;
          })
          .filter(Boolean)
          .join('\n');
      };

      const formatSkillsString = (val) => {
        if (!val) return '';
        if (Array.isArray(val)) {
          // Skill names only: the requirement score is stored separately and is no
          // longer appended to the editable field.
          return val
            .map(s => (typeof s === 'object' && s !== null ? (s.skill || s.name || '') : String(s)))
            .filter(Boolean)
            .join(', ');
        }
        return String(val);
      };

      setJobForm(prev => ({
        ...prev,
        description: data.description || prev.description,
        responsibilities: formatBulletList(data.responsibilities) || prev.responsibilities,
        requirements: formatBulletList(data.requirements) || prev.requirements,
        must_have_skills: formatSkillsString(data.must_have_skills) || prev.must_have_skills,
        good_to_have_skills: formatSkillsString(data.good_to_have_skills) || prev.good_to_have_skills
      }));
      showToast('✨ Role details, responsibilities, requirements & skills generated!');
    } catch (err) {
      console.error('AI generation error', err);
      showToast('Failed to generate description with AI', 'error');
    } finally {
      setGeneratingAiDesc(false);
    }
  };

  // AI JD Parser
  const handleJdFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setJobForm(prev => ({
      ...prev,
      jd_file: file,
      jd_file_name: file.name
    }));

    // Trigger AI parsing automatically
    setParsingJdFile(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      showToast('AI reading document & structuring into standard format...', 'info');
      const res = await api.post('/tpo/internal-jobs/parse-jd', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const data = res.data;

      const formatBulletList = (val) => {
        if (!val) return '';
        if (Array.isArray(val)) {
          return val
            .map(it => {
              const clean = String(it).trim();
              return clean.startsWith('•') || clean.startsWith('-') || clean.startsWith('*')
                ? clean
                : `• ${clean}`;
            })
            .join('\n');
        }
        return String(val);
      };

      const formatSkillsString = (val) => {
        if (!val) return '';
        if (Array.isArray(val)) {
          // Skill names only: the requirement score is stored separately and is no
          // longer appended to the editable field.
          return val
            .map(s => (typeof s === 'object' && s !== null ? (s.skill || s.name || '') : String(s)))
            .filter(Boolean)
            .join(', ');
        }
        return String(val);
      };

      setJobForm(prev => ({
        ...prev,
        title: prev.title.trim() ? prev.title : (data.title || ''),
        company_name: prev.company_name.trim() ? prev.company_name : (data.company_name || ''),
        description: data.description || prev.description,
        responsibilities: formatBulletList(data.responsibilities) || prev.responsibilities,
        requirements: formatBulletList(data.requirements) || prev.requirements,
        must_have_skills: formatSkillsString(data.must_have_skills) || prev.must_have_skills,
        good_to_have_skills: formatSkillsString(data.good_to_have_skills) || prev.good_to_have_skills,
        salary_package: data.salary_package || prev.salary_package,
        location: data.location || prev.location
      }));
      showToast('✨ JD successfully extracted and formatted with AI!');
    } catch (err) {
      console.error('AI JD Parsing error', err);
      showToast('Document attached. (AI auto-formatting skipped or failed)', 'info');
    } finally {
      setParsingJdFile(false);
    }
  };

  const handleCreateJobSubmit = async (e) => {
    e.preventDefault();
    if (!jobForm.title.trim()) {
      showToast('Job Title is required', 'error');
      return;
    }
    setCreatingJob(true);
    try {
      // Names come from the text fields; the requirement level (and whether it is a
      // TPO edit or an accepted suggestion) comes from the score editor. A skill
      // with no decided score is sent without one, so the backend calibrates it.
      const must_have_payload = buildSkillPayload(parseSkillNames(jobForm.must_have_skills), skillScores.must);
      const good_to_have_payload = buildSkillPayload(parseSkillNames(jobForm.good_to_have_skills), skillScores.good);

      const formData = new FormData();
      formData.append('title', jobForm.title);
      if (jobForm.company_name) formData.append('company_name', jobForm.company_name);
      if (jobForm.description) formData.append('description', jobForm.description);
      if (jobForm.responsibilities) formData.append('responsibilities', jobForm.responsibilities);
      if (jobForm.requirements) formData.append('requirements', jobForm.requirements);
      formData.append('must_have_skills', JSON.stringify(must_have_payload));
      formData.append('good_to_have_skills', JSON.stringify(good_to_have_payload));
      if (jobForm.salary_package) formData.append('salary_package', jobForm.salary_package);
      if (jobForm.location) formData.append('location', jobForm.location);
      formData.append('target_batches', JSON.stringify(jobForm.target_batches || ['All']));
      if (jobForm.status) formData.append('status', jobForm.status);
      if (jobForm.jd_file) {
        formData.append('jd_file', jobForm.jd_file);
      }

      if (editingJob) {
        const res = await api.put(`/tpo/internal-jobs/${editingJob.id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        const queued = res.data?.notifications_queued || 0;
        showToast(
          queued > 0
            ? `Campus job posting updated — email queued for ${queued} newly targeted student${queued === 1 ? '' : 's'}`
            : 'Campus job posting updated successfully!'
        );
      } else {
        const res = await api.post('/tpo/internal-jobs', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        const queued = res.data?.notifications_queued || 0;
        showToast(
          queued > 0
            ? `Campus job published — email queued for ${queued} matching student${queued === 1 ? '' : 's'}`
            : 'Campus job opportunity published successfully!'
        );
      }
      closeJobModal();
      fetchJobs();
    } catch (err) {
      console.error('Job submission failed', err);
      showToast(err.response?.data?.detail || (editingJob ? 'Failed to update job' : 'Failed to create job'), 'error');
    } finally {
      setCreatingJob(false);
    }
  };

  const resetJobForm = () => {
    setIsCustomLocation(false);
    setFileInputKey(k => k + 1);
    setSkillScores({ must: {}, good: {} });
    setJobForm({
      title: '',
      company_name: '',
      description: '',
      responsibilities: '',
      requirements: '',
      must_have_skills: '',
      good_to_have_skills: '',
      salary_package: 'Industry Standard',
      location: 'On-Site / Hybrid',
      target_batches: ['All'],
      status: 'ACTIVE',
      jd_file: null,
      jd_file_name: ''
    });
  };

  const openCreateModal = () => {
    setEditingJob(null);
    resetJobForm();
    setShowCreateModal(true);
  };

  const openEditModal = (job) => {
    setEditingJob(job);
    setSkillScores(scoresFromStoredSkills(job.must_have_skills, job.good_to_have_skills));
    const loc = job.location || 'On-Site / Hybrid';
    setIsCustomLocation(!POPULAR_LOCATIONS.includes(loc) && loc !== 'On-Site / Hybrid');
    setFileInputKey(k => k + 1);
    setJobForm({
      title: job.title || '',
      company_name: (job.company_name && job.company_name !== 'Campus Recruitment Partner') ? job.company_name : '',
      description: job.description || '',
      responsibilities: job.responsibilities || '',
      requirements: job.requirements || '',
      must_have_skills: Array.isArray(job.must_have_skills)
        ? job.must_have_skills.map(s => typeof s === 'object' && s !== null ? (s.skill || s.name || '') : s).filter(Boolean).join(', ')
        : (job.must_have_skills || ''),
      good_to_have_skills: Array.isArray(job.good_to_have_skills)
        ? job.good_to_have_skills.map(s => typeof s === 'object' && s !== null ? (s.skill || s.name || '') : s).filter(Boolean).join(', ')
        : (job.good_to_have_skills || ''),
      salary_package: job.salary_package || 'Industry Standard',
      location: loc,
      target_batches: job.target_batches || ['All'],
      status: job.status || 'ACTIVE',
      jd_file: null,
      jd_file_name: job.jd_file_name || (job.has_jd_file ? 'Attached JD Document' : '')
    });
    setShowCreateModal(true);
  };

  const closeJobModal = () => {
    setShowCreateModal(false);
    setEditingJob(null);
    resetJobForm();
  };

  // Requirement scores: one generation from the role + JD, displayed so the TPO can
  // see and override each level before the job reaches students.
  const generateSkillScores = async (values = jobForm) => {
    const mustNames = parseSkillNames(values.must_have_skills);
    const goodNames = parseSkillNames(values.good_to_have_skills);
    if (!String(values.title || '').trim() || (!mustNames.length && !goodNames.length)) return;
    setScoringSkills(true);
    try {
      const res = await api.post('/tpo/internal-jobs/score-skills', {
        title: values.title,
        description: values.description || '',
        responsibilities: values.responsibilities || '',
        requirements: values.requirements || '',
        must_have_skills: mustNames,
        good_to_have_skills: goodNames
      });
      const toMap = (rows) => Object.fromEntries(
        (rows || [])
          .filter(row => row && row.skill)
          .map(row => [String(row.skill).trim().toLowerCase(), { score: row.required_score, source: 'ai' }])
      );
      const generated = {
        must: toMap(res.data?.must_have_skills),
        good: toMap(res.data?.good_to_have_skills)
      };
      // Generated values never overwrite a level the TPO typed.
      setSkillScores(prev => mergeGeneratedScores(prev, generated));
    } catch (err) {
      console.error('Skill requirement scoring failed', err);
      showToast('Could not generate requirement scores automatically — you can type them in yourself.', 'info');
    } finally {
      setScoringSkills(false);
    }
  };

  const handleSkillScoreChange = (group, name, raw) => {
    setSkillScores(prev => ({ ...prev, [group]: updateSkillScoreMap(prev[group], name, raw) }));
  };

  const handleNotifyStudents = async (job) => {
    if (!job) return;
    try {
      const res = await api.post(`/tpo/internal-jobs/${job.id}/notify-students`);
      const queued = res.data?.queued || 0;
      const sent = res.data?.already_notified || 0;
      showToast(
        queued > 0
          ? `Email queued for ${queued} student${queued === 1 ? '' : 's'}${sent ? ` · ${sent} already notified` : ''}`
          : 'All eligible students have already been notified for this job',
        queued > 0 ? 'success' : 'info'
      );
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to queue student notifications', 'error');
    }
  };

  const handleDeleteJob = async (job) => {
    if (!window.confirm(`Are you sure you want to delete "${job.title}"? This will permanently remove this campus posting and student applications.`)) {
      return;
    }
    try {
      await api.delete(`/tpo/internal-jobs/${job.id}`);
      showToast('Job posting deleted successfully');
      if (selectedJob && selectedJob.id === job.id) {
        setSelectedJob(null);
        setSubTab('jobs');
      }
      fetchJobs();
    } catch (err) {
      console.error('Failed to delete job', err);
      showToast(err.response?.data?.detail || 'Failed to delete job', 'error');
    }
  };

  // Batch mapping handler
  const openBatchModal = (job) => {
    setBatchModalJob(job);
    setSelectedBatchesForJob(job.target_batches || ['All']);
  };

  const handleSaveBatches = async () => {
    if (!batchModalJob) return;
    setSavingBatches(true);
    try {
      const res = await api.put(`/tpo/internal-jobs/${batchModalJob.id}/batches`, {
        target_batches: selectedBatchesForJob
      });
      const queued = res.data?.notifications_queued || 0;
      showToast(
        queued > 0
          ? `Batch mapping updated — email queued for ${queued} newly targeted student${queued === 1 ? '' : 's'}`
          : 'Batch mapping updated successfully!'
      );
      setBatchModalJob(null);
      fetchJobs();
    } catch (err) {
      console.error('Failed to update batches', err);
      showToast('Failed to update batches', 'error');
    } finally {
      setSavingBatches(false);
    }
  };

  // Candidate decision (Approve / Disapprove)
  const handleCandidateDecision = async (interestId, status, remarks = null) => {
    if (status === 'REJECTED' && (!remarks || !remarks.trim())) {
      showToast('Remarks are mandatory when disapproving a candidate', 'error');
      return;
    }
    try {
      const payload = { status };
      if (remarks) payload.remarks = remarks.trim();
      await api.post(`/tpo/internal-jobs/candidates/${interestId}/decision`, payload);
      const actionLabel = status === 'APPROVED' ? 'Approved' : 'Disapproved';
      showToast(`Student application successfully ${actionLabel}!`);
      // Update local state
      setCandidates(prev => prev.map(c =>
        (c.interest_id === interestId || c.id === interestId)
          ? { ...c, status, ...(remarks ? { remarks: remarks.trim() } : {}) }
          : c
      ));
      setSelectedApplication(prev => prev && (prev.interest_id === interestId || prev.id === interestId)
        ? { ...prev, status, ...(remarks ? { remarks: remarks.trim() } : {}) }
        : prev
      );
    } catch (err) {
      console.error('Decision error', err);
      showToast(err.response?.data?.detail || 'Failed to update decision', 'error');
    }
  };

  const openDisapproveModal = (candidate) => {
    setDisapproveModalCandidate(candidate);
    setDisapproveRemarks(candidate.remarks || '');
  };

  const handleConfirmDisapprove = async () => {
    if (!disapproveModalCandidate) return;
    if (!disapproveRemarks.trim()) {
      showToast('Remarks are mandatory when disapproving a candidate', 'error');
      return;
    }
    const targetId = disapproveModalCandidate.interest_id || disapproveModalCandidate.id;
    setSavingDisapproval(true);
    try {
      await handleCandidateDecision(targetId, 'REJECTED', disapproveRemarks.trim());
      setDisapproveModalCandidate(null);
      setDisapproveRemarks('');
    } finally {
      setSavingDisapproval(false);
    }
  };

  // Candidate Remarks
  const openRemarksModal = (candidate) => {
    setRemarksModalCandidate(candidate);
    setRemarksText(candidate.remarks || '');
  };

  const handleSaveCandidateRemarks = async () => {
    if (!remarksModalCandidate) return;
    setSavingRemarks(true);
    const targetId = remarksModalCandidate.interest_id || remarksModalCandidate.id;
    try {
      await api.post(`/tpo/internal-jobs/candidates/${targetId}/remarks`, {
        remarks: remarksText
      });
      showToast('Remarks saved successfully!');
      const newStatus = remarksModalCandidate.status === 'INTERESTED' ? 'REMARKED' : remarksModalCandidate.status;
      setCandidates(prev => prev.map(c =>
        (c.interest_id === targetId || c.id === targetId)
          ? { ...c, remarks: remarksText, status: newStatus }
          : c
      ));
      setSelectedApplication(prev => prev && (prev.interest_id === targetId || prev.id === targetId)
        ? { ...prev, remarks: remarksText, status: newStatus }
        : prev
      );
      setRemarksModalCandidate(null);
    } catch (err) {
      console.error('Remarks error', err);
      showToast('Failed to save remarks', 'error');
    } finally {
      setSavingRemarks(false);
    }
  };

  // Download attached JD document
  const handleDownloadJd = async (jobId, e) => {
    e?.stopPropagation();
    try {
      showToast('Downloading job description...', 'info');
      const token = localStorage.getItem('token');
      const res = await api.get(`/tpo/internal-jobs/${jobId}/download-jd`, {
        params: token ? { token } : {},
        responseType: 'blob'
      });
      const blob = new Blob([res.data], {
        type: res.headers['content-type'] || 'application/pdf'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      let filename = `JD_Job_${jobId}.pdf`;
      const disposition = res.headers['content-disposition'];
      if (disposition && disposition.includes('filename=')) {
        const matches = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (matches && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showToast('Job description downloaded!', 'success');
    } catch (err) {
      console.error('Failed to download JD', err);
      let errorMsg = 'Failed to download JD Document';
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const json = JSON.parse(text);
          if (json.detail) errorMsg = json.detail;
        } catch (_) {}
      } else if (err.response?.data?.detail) {
        errorMsg = err.response.data.detail;
      }
      showToast(errorMsg, 'error');
    }
  };

  // Filter candidates
  const filteredCandidates = candidates.filter(c => {
    const matchSearch = (
      c.student_name.toLowerCase().includes(candidateSearch.toLowerCase()) ||
      c.email.toLowerCase().includes(candidateSearch.toLowerCase()) ||
      c.usn.toLowerCase().includes(candidateSearch.toLowerCase()) ||
      c.branch.toLowerCase().includes(candidateSearch.toLowerCase())
    );
    const matchStatus = candidateStatusFilter === 'All' || c.status === candidateStatusFilter;
    return matchSearch && matchStatus;
  });

  return (
    <div className="internal-jobs-wrapper">
      {/* Top Header & Navigation */}
      <div className="internal-jobs-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {subTab === 'candidates' && (
            <button
              onClick={() => { setSubTab('jobs'); setSelectedJob(null); }}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '8px 14px', borderRadius: '8px', border: '1px solid #cbd5e1',
                background: '#ffffff', color: '#334155', fontWeight: 600, cursor: 'pointer'
              }}
            >
              <ArrowLeft size={16} /> All Jobs
            </button>
          )}
          <div>
            <h2 style={{ fontSize: '1.4rem', fontWeight: 800, margin: 0, color: '#0f172a' }}>
              {subTab === 'candidates' && selectedJob
                ? `Interested Candidates: ${selectedJob.title}`
                : subTab === 'report' ? 'Campus Placement Report' : 'Campus Job Postings'}
            </h2>
            <div style={{ fontSize: '0.85rem', color: '#64748b', marginTop: '2px' }}>
              {subTab === 'candidates' && selectedJob
                ? `${selectedJob.company_name} • Batches: ${(selectedJob.target_batches || ['All']).join(', ')}`
                : 'Upload institution jobs, map to student batches, review scores & manage interested students'}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <div className="internal-jobs-nav">
            <button
              className={subTab === 'jobs' ? 'active' : ''}
              onClick={() => { setSubTab('jobs'); setSelectedJob(null); }}
            >
              <Briefcase size={16} /> Campus Jobs
            </button>
            {canReport && (
              <button
                className={subTab === 'report' ? 'active' : ''}
                onClick={() => { setSubTab('report'); fetchReport(); }}
              >
                <FileText size={16} /> Overall Report
              </button>
            )}
          </div>

          {canCreate && (
            <button className="internal-post-btn" onClick={openCreateModal}>
              <Plus size={18} /> Post Campus Job
            </button>
          )}
        </div>
      </div>

      {/* ─── TAB 1: CAMPUS JOBS ─── */}
      {subTab === 'jobs' && (
        <>
          {loadingJobs ? (
            <div style={{ textAlign: 'center', padding: '60px 0', color: '#64748b' }}>
              <Loader2 size={32} className="animate-spin" style={{ margin: '0 auto 12px' }} />
              Loading campus job openings...
            </div>
          ) : jobs.length === 0 ? (
            <div style={{
              background: '#ffffff', border: '2px dashed #cbd5e1', borderRadius: '16px',
              padding: '60px 24px', textAlign: 'center', color: '#64748b'
            }}>
              <Briefcase size={44} style={{ color: '#94a3b8', margin: '0 auto 16px' }} />
              <h3 style={{ fontSize: '1.15rem', fontWeight: 700, color: '#1e293b', marginBottom: '8px' }}>
                No Campus Jobs Posted Yet
              </h3>
              <p style={{ maxWidth: '420px', margin: '0 auto 20px', fontSize: '0.9rem', lineHeight: 1.5 }}>
                Post internal job openings with an attached JD document. Students in mapped batches can express interest directly.
              </p>
              {canCreate && (
                <button className="internal-post-btn" onClick={openCreateModal}>
                  <Plus size={16} /> Post Your First Job
                </button>
              )}
            </div>
          ) : (
            <div className="internal-jobs-grid">
              {jobs.map(job => (
                <div key={job.id} className="internal-job-card">
                  <div>
                    <div className="internal-job-card-header">
                      <div>
                        <h3 className="internal-job-title">{job.title}</h3>
                        <div className="internal-job-company">
                          <Building2 size={14} /> {job.company_name}
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{
                          padding: '4px 10px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 700,
                          background: job.status === 'ACTIVE' ? '#ecfdf5' : '#f1f5f9',
                          color: job.status === 'ACTIVE' ? '#065f46' : '#64748b'
                        }}>
                          {job.status}
                        </span>
                        <button
                          type="button"
                          onClick={() => openEditModal(job)}
                          title="Edit Job Posting"
                          style={{
                            background: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            color: '#475569',
                            cursor: 'pointer',
                            padding: '4px 8px',
                            borderRadius: '6px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            transition: 'all 0.15s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color = '#2563eb';
                            e.currentTarget.style.borderColor = '#93c5fd';
                            e.currentTarget.style.background = '#eff6ff';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color = '#475569';
                            e.currentTarget.style.borderColor = '#e2e8f0';
                            e.currentTarget.style.background = '#f8fafc';
                          }}
                        >
                          <Edit2 size={13} /> Edit
                        </button>
                      </div>
                    </div>

                    <p className="internal-job-brief">
                      {job.description || 'No description provided.'}
                    </p>

                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: '4px' }}>
                        Mapped Batches:
                      </div>
                      <div className="internal-batch-pills">
                        {(job.target_batches || ['All']).map((b, idx) => (
                          <span key={idx} className="internal-batch-pill">
                            {b}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div>
                    {/* Stats */}
                    <div className="internal-job-card-stats">
                      <div className="internal-card-stat">
                        <span className="internal-card-stat-num">{job.total_interested_count || 0}</span>
                        <span className="internal-card-stat-lbl">Interested</span>
                      </div>
                      <div className="internal-card-stat">
                        <span className="internal-card-stat-num" style={{ color: '#16a34a' }}>{job.approved_count || 0}</span>
                        <span className="internal-card-stat-lbl">Approved</span>
                      </div>
                      <div className="internal-card-stat">
                        <span className="internal-card-stat-num" style={{ color: '#dc2626' }}>{job.rejected_count || 0}</span>
                        <span className="internal-card-stat-lbl">Disapproved</span>
                      </div>
                      <div className="internal-card-stat">
                        <span className="internal-card-stat-num" style={{ color: '#d97706' }}>{job.pending_count || 0}</span>
                        <span className="internal-card-stat-lbl">Pending</span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="internal-job-card-actions">
                      <button
                        onClick={() => fetchJobCandidates(job)}
                        style={{
                          flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                          padding: '9px 14px', borderRadius: '8px', border: 'none', background: '#2563eb',
                          color: '#ffffff', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer'
                        }}
                      >
                        <Users size={15} /> View Interested ({job.total_interested_count || 0})
                      </button>

                      {canEditDelete && (
                        <>
                          <button
                            onClick={() => openEditModal(job)}
                            title="Edit Job Posting"
                            style={{
                              display: 'flex', alignItems: 'center', gap: '6px',
                              padding: '9px 12px', borderRadius: '8px', border: '1px solid #cbd5e1',
                              background: '#ffffff', color: '#475569', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer'
                            }}
                          >
                            <Edit2 size={15} /> Edit
                          </button>

                          <button
                            onClick={() => openBatchModal(job)}
                            title="Map Batches"
                            style={{
                              display: 'flex', alignItems: 'center', gap: '6px',
                              padding: '9px 12px', borderRadius: '8px', border: '1px solid #cbd5e1',
                              background: '#ffffff', color: '#475569', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer'
                            }}
                          >
                            <Layers size={15} /> Batches
                          </button>
                        </>
                      )}

                      {job.has_jd_file && (
                        <button
                          onClick={(e) => handleDownloadJd(job.id, e)}
                          title="Download JD Document"
                          style={{
                            padding: '9px 12px', borderRadius: '8px', border: '1px solid #cbd5e1',
                            background: '#eff6ff', color: '#2563eb', cursor: 'pointer'
                          }}
                        >
                          <Download size={15} />
                        </button>
                      )}

                      {canEditDelete && (
                        <button
                          onClick={() => handleNotifyStudents(job)}
                          title="Email this posting to targeted students (already-notified students are skipped)"
                          style={{
                            padding: '9px 12px', borderRadius: '8px', border: '1px solid #e0e7ff',
                            background: '#eef2ff', color: '#4338ca', cursor: 'pointer'
                          }}
                        >
                          <Mail size={15} />
                        </button>
                      )}

                      {canEditDelete && (
                        <button
                          onClick={() => handleDeleteJob(job)}
                          title="Delete Job Posting"
                          style={{
                            padding: '9px 12px', borderRadius: '8px', border: '1px solid #fee2e2',
                            background: '#fff5f5', color: '#dc2626', cursor: 'pointer'
                          }}
                        >
                          <Trash2 size={15} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ─── TAB 2: CANDIDATE REVIEW (DRILLDOWN) ─── */}
      {subTab === 'candidates' && selectedJob && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {selectedApplication ? (
            /* CANDIDATE APPLICATION DETAILS VIEW (SCREENSHOT 2 PARITY) */
            <div>
              <div style={{ marginBottom: '16px' }}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#0f172a', letterSpacing: '0.5px', textTransform: 'uppercase', margin: 0 }}>
                  CANDIDATE APPLICATION DETAILS
                </h2>
              </div>
              <div className="candidate-detail-card" style={{ background: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', padding: '32px', boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }}>
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '28px', borderBottom: '1px solid #e2e8f0', paddingBottom: '20px', flexWrap: 'wrap', gap: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <button
                    onClick={() => setSelectedApplication(null)}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#f8fafc', color: '#1e293b', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem' }}
                  >
                    <ArrowLeft size={16} /> Back to List
                  </button>
                  <div>
                    <h2 style={{ fontSize: '1.4rem', fontWeight: 700, margin: 0, color: '#0f172a' }}>{selectedApplication.student_name}</h2>
                    <div style={{ fontSize: '0.88rem', color: '#64748b', marginTop: '4px' }}>
                      Applied for: <strong style={{ color: 'var(--faculty-accent, #3b82f6)' }}>{selectedApplication.job_name || selectedJob.title}</strong> • {selectedApplication.email}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{
                    padding: '6px 14px',
                    borderRadius: '20px',
                    fontWeight: 700,
                    fontSize: '0.82rem',
                    background: String(selectedApplication.status).toUpperCase().includes('APPROV') ? 'rgba(16, 185, 129, 0.12)' : String(selectedApplication.status).toUpperCase().includes('REJECT') || String(selectedApplication.status).toUpperCase().includes('DISAPPROV') ? 'rgba(239, 68, 68, 0.12)' : String(selectedApplication.status).toUpperCase().includes('REMARK') ? 'rgba(59, 130, 246, 0.12)' : 'rgba(245, 158, 11, 0.12)',
                    color: String(selectedApplication.status).toUpperCase().includes('APPROV') ? '#10b981' : String(selectedApplication.status).toUpperCase().includes('REJECT') || String(selectedApplication.status).toUpperCase().includes('DISAPPROV') ? '#ef4444' : String(selectedApplication.status).toUpperCase().includes('REMARK') ? '#3b82f6' : '#d97706',
                    border: `1px solid ${String(selectedApplication.status).toUpperCase().includes('APPROV') ? 'rgba(16, 185, 129, 0.3)' : String(selectedApplication.status).toUpperCase().includes('REJECT') || String(selectedApplication.status).toUpperCase().includes('DISAPPROV') ? 'rgba(239, 68, 68, 0.3)' : String(selectedApplication.status).toUpperCase().includes('REMARK') ? 'rgba(59, 130, 246, 0.3)' : 'rgba(245, 158, 11, 0.3)'}`
                  }}>
                    Status: {String(selectedApplication.status).toUpperCase() === 'REJECTED' ? 'DISAPPROVED' : String(selectedApplication.status || 'INTERESTED').replace(/_/g, ' ')}
                  </span>
                  {canDecide && selectedApplication.status !== 'APPROVED' && (
                    <button
                      onClick={() => handleCandidateDecision(selectedApplication.interest_id || selectedApplication.id, 'APPROVED')}
                      style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 20px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg, #10b981, #059669)', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem', boxShadow: '0 4px 12px rgba(16, 185, 129, 0.25)' }}
                    >
                      <Check size={18} /> Approve Application
                    </button>
                  )}
                  {canDecide && selectedApplication.status !== 'REJECTED' && (
                    <button
                      onClick={() => openDisapproveModal(selectedApplication)}
                      style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 16px', borderRadius: '8px', border: '1px solid #fecaca', background: '#fef2f2', color: '#dc2626', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem' }}
                      title="Disapprove Student (Remarks Mandatory)"
                    >
                      <X size={18} /> Disapprove
                    </button>
                  )}
                  {canRemark && (
                    <button
                      onClick={() => openRemarksModal(selectedApplication)}
                      style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 20px', borderRadius: '8px', border: '1px solid #bfdbfe', background: '#eff6ff', color: '#2563eb', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem' }}
                    >
                      <MessageSquare size={18} /> Add Remarks
                    </button>
                  )}
                </div>
              </div>

              {/* Remarks Banner */}
              {selectedApplication.remarks && (
                <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '12px', padding: '16px 20px', marginBottom: '24px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <span style={{ fontWeight: 700, fontSize: '0.95rem', color: '#92400e', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <MessageSquare size={16} /> Latest Remarks
                    </span>
                    <span style={{ fontSize: '0.8rem', color: '#b45309' }}>
                      Added by {selectedApplication.remarks_added_by || 'TPO'} • {selectedApplication.remarks_added_at || 'Recently'}
                    </span>
                  </div>
                  <p style={{ margin: 0, color: '#78350f', fontSize: '0.9rem', lineHeight: 1.5 }}>
                    {selectedApplication.remarks}
                  </p>
                </div>
              )}

              {/* 2-Column Grid */}
              <div className="candidate-detail-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '24px' }}>
                {/* Column 1: Resume & Academic Details */}
                <div className="candidate-detail-panel" style={{ background: '#f8fafc', borderRadius: '14px', padding: '24px', border: '1px solid #e2e8f0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px', color: '#0f172a' }}>
                    <FileText size={22} style={{ color: 'var(--faculty-accent, #3b82f6)' }} />
                    <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>Resume & Academic Details</h3>
                  </div>
                  <div style={{ marginBottom: '16px', fontSize: '0.9rem', color: '#334155', lineHeight: 1.6 }}>
                    <p style={{ margin: '0 0 8px 0' }}><strong>Batch:</strong> {selectedApplication.batch || 'N/A'}</p>
                    <p style={{ margin: '0 0 8px 0' }}><strong>Department / Branch:</strong> {selectedApplication.branch || 'N/A'}</p>
                    <p style={{ margin: '0 0 8px 0' }}><strong>Applied Date:</strong> {selectedApplication.applied_at || selectedApplication.created_at || 'Recently'}</p>
                  </div>

                  {selectedApplication.resume_url ? (
                    <div style={{ marginTop: '16px' }}>
                      <button
                        onClick={async (e) => {
                          e.preventDefault();
                          const url = selectedApplication.resume_url;
                          if (url.startsWith('http')) {
                            window.open(url, '_blank');
                            return;
                          }
                          try {
                            const cleanPath = url.replace('/api/documents/', '').split('/').filter(Boolean).join('/');
                            const res = await api.get(`/api/documents/${cleanPath}`, { responseType: 'blob' });
                            const blobUrl = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                            const a = document.createElement('a');
                            a.href = blobUrl;
                            a.target = '_blank';
                            a.click();
                            setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
                          } catch (error) {
                            console.error('Error fetching resume:', error);
                            showToast('Failed to load resume document.', 'error');
                          }
                        }}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 18px', background: 'var(--faculty-accent, #3b82f6)', color: '#fff', borderRadius: '8px', textDecoration: 'none', fontWeight: 600, fontSize: '0.9rem', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}
                      >
                        <ExternalLink size={16} /> View Student Resume (PDF)
                      </button>
                    </div>
                  ) : (
                    <div style={{ padding: '12px', background: '#fff', borderRadius: '8px', border: '1px dashed #cbd5e1', color: '#64748b', fontSize: '0.85rem' }}>
                      No uploaded resume PDF found.
                    </div>
                  )}

                  {(selectedApplication.resume_summary || selectedApplication.resume_text || selectedApplication.id) && (
                    <div style={{ marginTop: '16px' }}>
                      <FormattedResumeSummary
                        summaryText={selectedApplication.resume_summary || selectedApplication.resume_text || "No resume summary available."}
                        title="AI Resume Summary & Competency Breakdown"
                        application={selectedApplication}
                      />
                    </div>
                  )}
                </div>

                {/* Column 2: AI Placement Recommendation & GitHub */}
                <div className="candidate-detail-side" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <AIPlacementRecommendationCard
                    application={selectedApplication}
                    onUpdateApplication={(updatedFields) => {
                      setSelectedApplication(prev => prev ? { ...prev, ...updatedFields } : prev);
                    }}
                    onApprove={() => handleCandidateDecision(selectedApplication.interest_id || selectedApplication.id, 'APPROVED')}
                    onRemark={() => openRemarksModal(selectedApplication)}
                  />

                  <div className="github-panel" style={{ background: '#f8fafc', borderRadius: '14px', padding: '18px', border: '1px solid #e2e8f0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px', color: '#0f172a' }}>
                      <GitBranch size={22} style={{ color: '#8b5cf6' }} />
                      <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>GitHub Profile & Repositories</h3>
                    </div>
                    {selectedApplication.github_url && (
                      <div style={{ marginBottom: '16px' }}>
                        <a
                          href={
                            selectedApplication.github_url.startsWith('http')
                              ? selectedApplication.github_url
                              : selectedApplication.github_url.includes('github.com')
                              ? `https://${selectedApplication.github_url}`
                              : `https://github.com/${selectedApplication.github_url.replace(/^\/+/, '')}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '8px 16px', background: '#24292e', color: '#fff', borderRadius: '8px', textDecoration: 'none', fontWeight: 600, fontSize: '0.88rem' }}
                        >
                          <ExternalLink size={16} /> Visit GitHub Profile
                        </a>
                      </div>
                    )}
                    {Array.isArray(selectedApplication.github_projects) && selectedApplication.github_projects.length > 0 ? (
                      <div>
                        <strong style={{ display: 'block', fontSize: '0.88rem', color: '#64748b', marginBottom: '10px' }}>Analyzed Projects:</strong>
                        <div className="github-project-list" style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '220px', overflowY: 'auto' }}>
                          {selectedApplication.github_projects.map((proj, idx) => {
                            const projUrl = proj.url || proj.repoUrl || proj.html_url || proj.liveUrl || '';
                            return (
                              <div key={idx} className="github-project-card" style={{ padding: '10px 12px', background: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                                  <div style={{ fontWeight: 600, color: '#0f172a', fontSize: '0.9rem' }}>
                                    {proj.name || proj.title || proj.repo || `Project #${idx+1}`}
                                  </div>
                                  {projUrl && (
                                    <a
                                      href={projUrl.startsWith('http') ? projUrl : `https://${projUrl}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{ color: '#8b5cf6', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '0.78rem', fontWeight: 600, flexShrink: 0 }}
                                    >
                                      <ExternalLink size={13} /> View Repo
                                    </a>
                                  )}
                                </div>
                                {proj.description && <div style={{ fontSize: '0.82rem', color: '#64748b', marginTop: '4px', lineHeight: 1.4 }}>{proj.description}</div>}
                                {proj.language && <span style={{ display: 'inline-block', marginTop: '6px', fontSize: '0.75rem', background: '#f1f5f9', color: '#334155', padding: '2px 8px', borderRadius: '4px', fontWeight: 600 }}>{proj.language}</span>}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <div style={{ padding: '12px', background: '#fff', borderRadius: '8px', border: '1px dashed #cbd5e1', color: '#64748b', fontSize: '0.85rem' }}>
                        No GitHub repositories indexed.
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Skills and Tools Row */}
              {(() => {
                let skillsList = [];
                if (Array.isArray(selectedApplication.ai_skillset) && selectedApplication.ai_skillset.length > 0) {
                  skillsList = selectedApplication.ai_skillset.map(s => typeof s === 'object' ? (s.name || s.skill || JSON.stringify(s)) : s);
                } else if (selectedApplication.skills) {
                  skillsList = typeof selectedApplication.skills === 'string' ? selectedApplication.skills.split(',').map(s => s.trim()).filter(Boolean) : [];
                }

                let toolsList = [];
                if (Array.isArray(selectedApplication.ai_toolset) && selectedApplication.ai_toolset.length > 0) {
                  toolsList = selectedApplication.ai_toolset.map(t => typeof t === 'object' ? (t.name || t.tool || JSON.stringify(t)) : t);
                }

                if (skillsList.length === 0 && toolsList.length === 0) return null;

                return (
                  <div style={{ marginTop: '24px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '24px' }}>
                    {skillsList.length > 0 && (
                      <div style={{ background: '#f8fafc', borderRadius: '14px', padding: '24px', border: '1px solid #e2e8f0' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px', color: '#0f172a' }}>
                          <Sparkles size={22} style={{ color: '#10b981' }} />
                          <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>AI-Analyzed Skills</h3>
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                          {skillsList.map((sk, idx) => (
                            <span key={idx} style={{ background: 'rgba(16, 185, 129, 0.12)', color: '#10b981', padding: '6px 14px', borderRadius: '20px', fontSize: '0.85rem', fontWeight: 600, border: '1px solid rgba(16, 185, 129, 0.3)' }}>
                              {sk}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {toolsList.length > 0 && (
                      <div style={{ background: '#f8fafc', borderRadius: '14px', padding: '24px', border: '1px solid #e2e8f0' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px', color: '#0f172a' }}>
                          <Terminal size={22} style={{ color: '#3b82f6' }} />
                          <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>AI-Analyzed Tools & Tech Stack</h3>
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                          {toolsList.map((tl, idx) => (
                            <span key={idx} style={{ background: 'rgba(59, 130, 246, 0.12)', color: '#3b82f6', padding: '6px 14px', borderRadius: '20px', fontSize: '0.85rem', fontWeight: 600, border: '1px solid rgba(59, 130, 246, 0.3)' }}>
                              {tl}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
              </div>
            </div>
          ) : (
            <>
              {/* CANDIDATE FIT RANKING (SCREENSHOT 1 PARITY) */}
              <CandidateRankingPanel
                jobId={selectedJob.id}
                roleName={selectedJob.title}
                isInternalJob={true}
                onSelectCandidate={(applicationId) => {
                  const found = candidates.find(c => (c.interest_id || c.id) === applicationId);
                  if (found) {
                    setSelectedApplication(found);
                  }
                }}
              />

              {/* CANDIDATE LIST TABLE */}
              <div className="internal-table-card">
                <div className="internal-table-header">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', flex: 1 }}>
                    <div style={{ position: 'relative', minWidth: '240px', flex: 1, maxWidth: '380px' }}>
                      <Search size={16} style={{ position: 'absolute', left: '12px', top: '12px', color: '#94a3b8' }} />
                      <input
                        type="text"
                        placeholder="Search student, email, USN, branch..."
                        value={candidateSearch}
                        onChange={e => setCandidateSearch(e.target.value)}
                        className="internal-input"
                        style={{ paddingLeft: '36px' }}
                      />
                    </div>

                    <select
                      value={candidateStatusFilter}
                      onChange={e => setCandidateStatusFilter(e.target.value)}
                      className="internal-input"
                      style={{ width: 'auto', minWidth: '150px' }}
                    >
                      <option value="All">All Statuses</option>
                      <option value="INTERESTED">Interested (New)</option>
                      <option value="APPROVED">Approved / Shortlisted</option>
                      <option value="REJECTED">Disapproved</option>
                      <option value="REMARKED">Remarked</option>
                    </select>
                  </div>

                  <div style={{ fontSize: '0.88rem', color: '#64748b', fontWeight: 600 }}>
                    Showing {filteredCandidates.length} of {candidates.length} candidates
                  </div>
                </div>

                {loadingCandidates ? (
                  <div style={{ textAlign: 'center', padding: '60px 0', color: '#64748b' }}>
                    <Loader2 size={32} className="animate-spin" style={{ margin: '0 auto 12px' }} />
                    Loading interested candidates...
                  </div>
                ) : filteredCandidates.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '60px 24px', color: '#64748b' }}>
                    <Users size={40} style={{ color: '#cbd5e1', margin: '0 auto 12px' }} />
                    <h4 style={{ margin: '0 0 6px 0', color: '#1e293b' }}>No candidates found</h4>
                    <p style={{ margin: 0, fontSize: '0.85rem' }}>No student has expressed interest matching this filter.</p>
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table className="internal-table">
                      <thead>
                        <tr>
                          <th>Student Name</th>
                          <th>USN & Branch</th>
                          <th>Batch</th>
                          <th>Status</th>
                          <th>Remarks</th>
                          <th style={{ textAlign: 'right' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredCandidates.map(c => (
                          <tr key={c.interest_id} style={{ cursor: 'pointer' }} onClick={() => setSelectedApplication(c)}>
                            <td>
                              <div style={{ fontWeight: 700, color: '#0f172a' }}>{c.student_name}</div>
                              <div style={{ fontSize: '0.78rem', color: '#64748b' }}>{c.email}</div>
                            </td>
                            <td>
                              <div style={{ fontWeight: 600 }}>{c.usn}</div>
                              <div style={{ fontSize: '0.78rem', color: '#64748b' }}>{c.branch}</div>
                            </td>
                            <td>
                              <span className="internal-batch-pill">{c.batch}</span>
                            </td>
                            <td>
                              <span className={`internal-badge ${
                                c.status === 'APPROVED' ? 'internal-badge-approved' :
                                c.status === 'REJECTED' ? 'internal-badge-rejected' :
                                c.status === 'REMARKED' ? 'internal-badge-remarked' : 'internal-badge-interested'
                              }`}>
                                {c.status === 'REJECTED' ? 'DISAPPROVED' : c.status}
                              </span>
                            </td>
                            <td style={{ maxWidth: '240px' }} onClick={e => e.stopPropagation()}>
                              {c.remarks ? (
                                <div style={{ fontSize: '0.82rem', color: '#475569', lineHeight: 1.4 }}>
                                  💬 {c.remarks}
                                </div>
                              ) : (
                                <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>None</span>
                              )}
                            </td>
                            <td onClick={e => e.stopPropagation()}>
                              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', alignItems: 'center' }}>
                                <button
                                  onClick={() => setSelectedApplication(c)}
                                  title="Review Candidate Application"
                                  style={{
                                    display: 'flex', alignItems: 'center', gap: '4px',
                                    padding: '6px 14px', borderRadius: '8px', border: '1px solid #cbd5e1',
                                    background: '#ffffff', color: '#0f172a', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer'
                                  }}
                                >
                                  <Eye size={14} /> Review
                                </button>

                                {canDecide && c.status !== 'APPROVED' && (
                                  <button
                                    onClick={() => handleCandidateDecision(c.interest_id, 'APPROVED')}
                                    title="Approve / Shortlist Student"
                                    style={{
                                      display: 'flex', alignItems: 'center', gap: '4px',
                                      padding: '6px 12px', borderRadius: '6px', border: 'none',
                                      background: '#10b981', color: '#ffffff', fontWeight: 600, fontSize: '0.78rem', cursor: 'pointer'
                                    }}
                                  >
                                    <Check size={14} /> Approve
                                  </button>
                                )}

                                {canDecide && c.status !== 'REJECTED' && (
                                  <button
                                    onClick={() => openDisapproveModal(c)}
                                    title="Disapprove Student (Remarks Mandatory)"
                                    style={{
                                      display: 'flex', alignItems: 'center', gap: '4px',
                                      padding: '6px 12px', borderRadius: '6px', border: 'none',
                                      background: '#ef4444', color: '#ffffff', fontWeight: 600, fontSize: '0.78rem', cursor: 'pointer'
                                    }}
                                  >
                                    <X size={14} /> Disapprove
                                  </button>
                                )}

                                {canRemark && (
                                  <button
                                    onClick={() => openRemarksModal(c)}
                                    title="Add Feedback Remarks"
                                    style={{
                                      display: 'flex', alignItems: 'center', gap: '4px',
                                      padding: '6px 12px', borderRadius: '6px', border: '1px solid #cbd5e1',
                                      background: '#ffffff', color: '#2563eb', fontWeight: 600, fontSize: '0.78rem', cursor: 'pointer'
                                    }}
                                  >
                                    <MessageSquare size={14} /> Remarks
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* ─── TAB 3: OVERALL REPORT & DOWNLOAD ─── */}
      {subTab === 'report' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Summary Stat Cards */}
          {reportData?.summary && (
            <div className="internal-stats-grid">
              <div className="internal-stat-card">
                <div className="internal-stat-icon" style={{ background: '#dbeafe', color: '#2563eb' }}>
                  <Briefcase size={22} />
                </div>
                <div>
                  <div className="internal-stat-val">{reportData.summary.total_jobs || 0}</div>
                  <div className="internal-stat-label">Total Jobs</div>
                </div>
              </div>

              <div className="internal-stat-card">
                <div className="internal-stat-icon" style={{ background: '#fef3c7', color: '#d97706' }}>
                  <Users size={22} />
                </div>
                <div>
                  <div className="internal-stat-val">{reportData.summary.total_interests || 0}</div>
                  <div className="internal-stat-label">Total Interested</div>
                </div>
              </div>

              <div className="internal-stat-card">
                <div className="internal-stat-icon" style={{ background: '#dcfce7', color: '#16a34a' }}>
                  <CheckCircle size={22} />
                </div>
                <div>
                  <div className="internal-stat-val">{reportData.summary.total_shortlisted || 0}</div>
                  <div className="internal-stat-label">Shortlisted</div>
                </div>
              </div>

              <div className="internal-stat-card">
                <div className="internal-stat-icon" style={{ background: '#fee2e2', color: '#dc2626' }}>
                  <XCircle size={22} />
                </div>
                <div>
                  <div className="internal-stat-val">{reportData.summary.total_rejected || 0}</div>
                  <div className="internal-stat-label">Disapproved</div>
                </div>
              </div>
            </div>
          )}

          {/* Action Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
            <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 800, color: '#0f172a' }}>
              Student Candidates Master Table
            </h3>
            <button
              onClick={handleExportCsv}
              disabled={exportingReport}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '8px',
                padding: '10px 20px', background: '#10b981', color: '#ffffff',
                border: 'none', borderRadius: '10px', fontWeight: 700, fontSize: '0.9rem',
                cursor: exportingReport ? 'not-allowed' : 'pointer', boxShadow: '0 4px 12px rgba(16, 185, 129, 0.25)'
              }}
            >
              {exportingReport ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
              {exportingReport ? 'Generating CSV...' : 'Download Report (CSV)'}
            </button>
          </div>

          {/* Master Table */}
          <div className="internal-table-card">
            {loadingReport ? (
              <div style={{ textAlign: 'center', padding: '60px 0', color: '#64748b' }}>
                <Loader2 size={32} className="animate-spin" style={{ margin: '0 auto 12px' }} />
                Loading comprehensive report...
              </div>
            ) : (!reportData?.candidate_records || reportData.candidate_records.length === 0) ? (
              <div style={{ textAlign: 'center', padding: '60px 24px', color: '#64748b' }}>
                <FileText size={40} style={{ color: '#cbd5e1', margin: '0 auto 12px' }} />
                <h4 style={{ margin: '0 0 6px 0', color: '#1e293b' }}>No candidate records yet</h4>
                <p style={{ margin: 0, fontSize: '0.85rem' }}>Once students express interest in campus jobs, their interest and statuses will appear here.</p>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="internal-table">
                  <thead>
                    <tr>
                      <th>Student</th>
                      <th>USN</th>
                      <th>Batch</th>
                      <th>Job Opportunity</th>
                      <th>Company</th>
                      <th>Status</th>
                      <th>TPO Remarks</th>
                      <th>Interest Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.candidate_records.map((r, i) => (
                      <tr key={i}>
                        <td>
                          <div style={{ fontWeight: 700 }}>{r.student_name}</div>
                          <div style={{ fontSize: '0.78rem', color: '#64748b' }}>{r.email}</div>
                        </td>
                        <td>{r.usn}</td>
                        <td><span className="internal-batch-pill">{r.batch}</span></td>
                        <td style={{ fontWeight: 600 }}>{r.job_title}</td>
                        <td>{r.company_name}</td>
                        <td>
                          <span className={`internal-badge ${
                            r.status === 'APPROVED' ? 'internal-badge-approved' :
                            r.status === 'REJECTED' ? 'internal-badge-rejected' :
                            r.status === 'REMARKED' ? 'internal-badge-remarked' : 'internal-badge-interested'
                          }`}>
                            {r.status === 'REJECTED' ? 'DISAPPROVED' : r.status}
                          </span>
                        </td>
                        <td style={{ maxWidth: '200px', fontSize: '0.82rem' }}>
                          {r.remarks || '—'}
                        </td>
                        <td style={{ fontSize: '0.82rem', color: '#64748b' }}>{r.created_at}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── MODAL 1: POST NEW CAMPUS JOB ─── */}
      {showCreateModal && (
        <div className="internal-modal-overlay">
          <div className="internal-modal-box">
            <div className="internal-modal-header">
              <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 800, color: '#0f172a' }}>
                {editingJob ? 'Edit Campus Job Posting' : 'Post New Campus Job'}
              </h3>
              <button
                onClick={closeJobModal}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center' }}
                title="Close"
              >
                <X size={20} color="#64748b" />
              </button>
            </div>

            <form onSubmit={handleCreateJobSubmit} className="internal-modal-form">
              <div className="internal-modal-body">
                {/* 1. Job Title & Company Name (and Status if editing) */}
                <div style={{ display: 'grid', gridTemplateColumns: editingJob ? '1fr 1fr 140px' : '1fr 1fr', gap: '16px' }}>
                  <div className="internal-form-group">
                    <label className="internal-form-label">Job Title *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Software Engineer - Backend"
                      value={jobForm.title}
                      onChange={e => setJobForm(prev => ({ ...prev, title: e.target.value }))}
                      className="internal-input"
                    />
                  </div>

                  <div className="internal-form-group">
                    <label className="internal-form-label">Company Name (Optional)</label>
                    <input
                      type="text"
                      placeholder="e.g. Infosys, TCS, or leave empty"
                      value={jobForm.company_name}
                      onChange={e => setJobForm(prev => ({ ...prev, company_name: e.target.value }))}
                      className="internal-input"
                    />
                  </div>

                  {editingJob && (
                    <div className="internal-form-group">
                      <label className="internal-form-label">Status</label>
                      <select
                        value={jobForm.status || 'ACTIVE'}
                        onChange={e => setJobForm(prev => ({ ...prev, status: e.target.value }))}
                        className="internal-input"
                      >
                        <option value="ACTIVE">ACTIVE</option>
                        <option value="CLOSED">CLOSED</option>
                      </select>
                    </div>
                  )}
                </div>

                {/* 2. Upload JD Box */}
                <div className="internal-form-group">
                  <div className="internal-form-label">
                    <span>Upload JD Document (PDF or Word)</span>
                    {parsingJdFile && (
                      <span style={{ color: '#7c3aed', fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Loader2 size={13} className="animate-spin" /> AI Extracting & Formatting...
                      </span>
                    )}
                  </div>
                  <label className="internal-upload-zone" style={{ position: 'relative' }}>
                    <UploadCloud size={28} style={{ color: '#3b82f6', margin: '0 auto 8px' }} />
                    {jobForm.jd_file_name ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '0.9rem', fontWeight: 600, color: '#1e293b' }}>
                          Attached: {jobForm.jd_file_name}
                        </span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setJobForm(prev => ({ ...prev, jd_file: null, jd_file_name: '' }));
                          }}
                          style={{
                            background: '#fee2e2',
                            color: '#dc2626',
                            border: 'none',
                            borderRadius: '50%',
                            width: '20px',
                            height: '20px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            lineHeight: 1
                          }}
                          title="Remove attached file"
                        >
                          ×
                        </button>
                      </div>
                    ) : (
                      <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#1e293b' }}>
                        Click or drop JD file here (PDF/DOCX)
                      </div>
                    )}
                    <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: '4px' }}>
                      ✨ AI will automatically read & structure the description, responsibilities, requirements & skills!
                    </div>
                    <input
                      key={fileInputKey}
                      type="file"
                      accept=".pdf,.docx,.doc"
                      onChange={handleJdFileUpload}
                      style={{ display: 'none' }}
                    />
                  </label>
                </div>

                {/* 3. Brief Role Description */}
                <div className="internal-form-group">
                  <div className="internal-form-label">
                    <span>Brief Role Description</span>
                    <button
                      type="button"
                      onClick={handleGenerateAiDescription}
                      disabled={generatingAiDesc || !jobForm.title.trim()}
                      className="internal-ai-btn"
                    >
                      {generatingAiDesc ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                      {generatingAiDesc ? 'Generating...' : '✨ Generate with AI'}
                    </button>
                  </div>
                  <textarea
                    rows={3}
                    placeholder="Brief description or purpose of the role..."
                    value={jobForm.description}
                    onChange={e => setJobForm(prev => ({ ...prev, description: e.target.value }))}
                    className="internal-textarea"
                  />
                </div>

                {/* 4. Key Responsibilities */}
                <div className="internal-form-group">
                  <label className="internal-form-label">Key Responsibilities</label>
                  <textarea
                    rows={3}
                    placeholder={"• Lead development of core modules\n• Collaborate with cross-functional technical teams"}
                    value={jobForm.responsibilities}
                    onChange={e => setJobForm(prev => ({ ...prev, responsibilities: e.target.value }))}
                    className="internal-textarea"
                  />
                </div>

                {/* 5. Requirements & Eligibility */}
                <div className="internal-form-group">
                  <label className="internal-form-label">Requirements & Eligibility</label>
                  <textarea
                    rows={3}
                    placeholder={"• B.Tech / BE in CSE, ISE or related field\n• Minimum 6.5 CGPA with no active backlogs"}
                    value={jobForm.requirements}
                    onChange={e => setJobForm(prev => ({ ...prev, requirements: e.target.value }))}
                    className="internal-textarea"
                  />
                </div>

                {/* 6. Skills */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div className="internal-form-group">
                    <label className="internal-form-label">Must-Have Skills (comma-separated)</label>
                    <input
                      type="text"
                      placeholder="Python, SQL, React"
                      value={jobForm.must_have_skills}
                      onChange={e => setJobForm(prev => ({ ...prev, must_have_skills: e.target.value }))}
                      onBlur={() => generateSkillScores()}
                      className="internal-input"
                    />
                  </div>

                  <div className="internal-form-group">
                    <label className="internal-form-label">Good-to-Have Skills (comma-separated)</label>
                    <input
                      type="text"
                      placeholder="Docker, Git, AWS"
                      value={jobForm.good_to_have_skills}
                      onChange={e => setJobForm(prev => ({ ...prev, good_to_have_skills: e.target.value }))}
                      onBlur={() => generateSkillScores()}
                      className="internal-input"
                    />
                  </div>
                </div>

                {/* 6b. Requirement scores — generated from the role + JD, editable */}
                {(parseSkillNames(jobForm.must_have_skills).length > 0 || parseSkillNames(jobForm.good_to_have_skills).length > 0) && (
                  <div className="internal-form-group" data-testid="skill-score-editor">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                      <label className="internal-form-label" style={{ margin: 0 }}>Requirement Scores (1–10)</label>
                      <button
                        type="button"
                        onClick={() => generateSkillScores()}
                        disabled={scoringSkills || !String(jobForm.title || '').trim()}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 12px',
                          borderRadius: '8px', border: '1px solid #c7d2fe', background: '#eef2ff',
                          color: '#3730a3', fontSize: '12.5px', fontWeight: 700,
                          cursor: scoringSkills ? 'progress' : 'pointer', opacity: scoringSkills ? 0.7 : 1
                        }}
                      >
                        {scoringSkills ? 'Scoring…' : '✨ Generate scores'}
                      </button>
                    </div>
                    <p style={{ margin: '4px 0 10px', fontSize: '12px', color: '#64748b' }}>
                      Generated from the job title and description, and used as the “Job Requirement” levels in each
                      student’s Skill Gap graph. Edit any value to override it.
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))', gap: '16px' }}>
                      {[
                        { key: 'must', label: 'Must-Have', value: jobForm.must_have_skills },
                        { key: 'good', label: 'Good-to-Have', value: jobForm.good_to_have_skills }
                      ].map(group => (
                        <div key={group.key} style={{ minWidth: 0 }}>
                          <div style={{ fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', color: '#64748b', marginBottom: '4px' }}>
                            {group.label}
                          </div>
                          {parseSkillNames(group.value).length === 0 ? (
                            <div style={{ fontSize: '12px', color: '#94a3b8', padding: '6px 0' }}>No skills added yet.</div>
                          ) : parseSkillNames(group.value).map(name => {
                            const entry = skillScores[group.key]?.[name.toLowerCase()];
                            return (
                              <div key={name} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 0', borderBottom: '1px solid #f1f5f9' }}>
                                <span style={{ flex: 1, minWidth: 0, fontSize: '13px', color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={name}>
                                  {name}
                                </span>
                                <span style={{ flexShrink: 0, fontSize: '10.5px', fontWeight: 700, color: entry?.source === 'tpo' ? '#b45309' : '#94a3b8', minWidth: '46px', textAlign: 'right' }}>
                                  {entry?.source === 'tpo' ? 'Edited' : (entry?.score !== null && entry?.score !== undefined ? 'AI' : '—')}
                                </span>
                                <input
                                  type="number"
                                  min="1"
                                  max="10"
                                  step="0.5"
                                  value={entry?.score ?? ''}
                                  onChange={e => handleSkillScoreChange(group.key, name, e.target.value)}
                                  className="internal-input"
                                  style={{ width: '86px', padding: '6px 8px', flexShrink: 0 }}
                                  aria-label={`${group.label} requirement score for ${name}`}
                                />
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* 7. Salary / CTC & Job Location */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div className="internal-form-group">
                    <label className="internal-form-label">Salary / CTC (Optional)</label>
                    <input
                      type="text"
                      placeholder="e.g. 6-8 LPA, Industry Standard, or ₹25k/mo"
                      value={jobForm.salary_package}
                      onChange={e => setJobForm(prev => ({ ...prev, salary_package: e.target.value }))}
                      className="internal-input"
                    />
                  </div>

                  <div className="internal-form-group">
                    <label className="internal-form-label">Job Location (Optional)</label>
                    <select
                      value={isCustomLocation ? 'custom' : (POPULAR_LOCATIONS.includes(jobForm.location) ? jobForm.location : (jobForm.location ? jobForm.location : 'On-Site / Hybrid'))}
                      onChange={e => {
                        const val = e.target.value;
                        if (val === 'custom') {
                          setIsCustomLocation(true);
                          setJobForm(prev => ({ ...prev, location: '' }));
                        } else {
                          setIsCustomLocation(false);
                          setJobForm(prev => ({ ...prev, location: val }));
                        }
                      }}
                      className="internal-input"
                    >
                      <option value="On-Site / Hybrid">On-Site / Hybrid (Default)</option>
                      <option value="On-Site">On-Site</option>
                      <option value="Hybrid">Hybrid</option>
                      <option value="Remote">Remote</option>
                      <optgroup label="Popular Tech Hubs">
                        <option value="Bengaluru">Bengaluru</option>
                        <option value="Hyderabad">Hyderabad</option>
                        <option value="Pune">Pune</option>
                        <option value="Delhi NCR / Gurgaon / Noida">Delhi NCR / Gurgaon / Noida</option>
                        <option value="Mumbai">Mumbai</option>
                        <option value="Chennai">Chennai</option>
                        <option value="Kolkata">Kolkata</option>
                        <option value="Ahmedabad">Ahmedabad</option>
                        <option value="Chandigarh">Chandigarh</option>
                        <option value="Coimbatore">Coimbatore</option>
                        <option value="Jaipur">Jaipur</option>
                      </optgroup>
                      {jobForm.location && !POPULAR_LOCATIONS.includes(jobForm.location) && !isCustomLocation && (
                        <option value={jobForm.location}>{jobForm.location} (Detected)</option>
                      )}
                      <option value="custom">Other / Custom Location...</option>
                    </select>
                    {isCustomLocation && (
                      <input
                        type="text"
                        placeholder="Type custom location (e.g. Mysuru, Kochi, Singapore)"
                        value={jobForm.location}
                        onChange={e => setJobForm(prev => ({ ...prev, location: e.target.value }))}
                        className="internal-input"
                        style={{ marginTop: '6px' }}
                        autoFocus
                      />
                    )}
                  </div>
                </div>

                {/* 8. Map to Batches Multi-Select Dropdown */}
                <div className="internal-form-group">
                  <label className="internal-form-label">Map to Batches *</label>
                  <BatchMultiSelectDropdown
                    selectedBatches={jobForm.target_batches}
                    onChange={(newBatches) => setJobForm(prev => ({ ...prev, target_batches: newBatches }))}
                    availableBatches={availableBatches}
                    placeholder="Click to select targeted student batches..."
                  />
                  <span style={{ fontSize: '0.76rem', color: '#64748b', marginTop: '2px' }}>
                    Only students belonging to the selected batches will see this campus opening.
                  </span>
                </div>
              </div>

              <div className="internal-modal-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <button
                  type="button"
                  onClick={() => {
                    resetJobForm();
                    showToast('All fields cleared', 'info');
                  }}
                  className="internal-btn-clear"
                  title="Clear all fields and reset form"
                >
                  <RotateCcw size={14} />
                  Clear All
                </button>

                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <button
                    type="button"
                    onClick={closeJobModal}
                    style={{
                      padding: '9px 18px', borderRadius: '8px', border: '1px solid #cbd5e1',
                      background: '#ffffff', color: '#475569', fontWeight: 600, cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={creatingJob || parsingJdFile}
                    className="internal-post-btn"
                  >
                    {creatingJob ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <Check size={16} />
                    )}
                    {creatingJob
                      ? (editingJob ? 'Saving Changes...' : 'Publishing...')
                      : (editingJob ? 'Save Changes' : 'Publish Campus Job')}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─── MODAL 2: MANAGE BATCHES ─── */}
      {batchModalJob && (
        <div className="internal-modal-overlay">
          <div className="internal-modal-box" style={{ maxWidth: '480px' }}>
            <div className="internal-modal-header">
              <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 800, color: '#0f172a' }}>
                Map Batches: {batchModalJob.title}
              </h3>
              <button onClick={() => setBatchModalJob(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={18} color="#64748b" />
              </button>
            </div>

            <div className="internal-modal-body" style={{ minHeight: '160px' }}>
              <p style={{ margin: 0, fontSize: '0.88rem', color: '#64748b' }}>
                Select which academic batches can view and show interest in this job opening:
              </p>

              <div style={{ marginTop: '8px' }}>
                <label className="internal-form-label" style={{ marginBottom: '6px' }}>Target Batches</label>
                <BatchMultiSelectDropdown
                  selectedBatches={selectedBatchesForJob}
                  onChange={(newBatches) => setSelectedBatchesForJob(newBatches)}
                  availableBatches={availableBatches}
                  placeholder="Select batches..."
                />
              </div>
            </div>

            <div className="internal-modal-footer">
              <button
                onClick={() => setBatchModalJob(null)}
                style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveBatches}
                disabled={savingBatches}
                className="internal-post-btn"
              >
                {savingBatches ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
                {savingBatches ? 'Saving...' : 'Save Batches'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── MODAL 3: ADD REMARKS ─── */}
      {remarksModalCandidate && (
        <div className="internal-modal-overlay">
          <div className="internal-modal-box" style={{ maxWidth: '480px' }}>
            <div className="internal-modal-header">
              <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 800, color: '#0f172a' }}>
                Candidate Remarks
              </h3>
              <button onClick={() => setRemarksModalCandidate(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={18} color="#64748b" />
              </button>
            </div>

            <div className="internal-modal-body">
              <div style={{ fontSize: '0.88rem', color: '#334155', marginBottom: '8px' }}>
                Providing remarks for: <strong>{remarksModalCandidate.student_name}</strong>
              </div>
              <textarea
                rows={4}
                placeholder="Enter feedback or instructions for the student (e.g. 'Shortlisted for technical round on Friday. Prepare system design.')..."
                value={remarksText}
                onChange={e => setRemarksText(e.target.value)}
                className="internal-textarea"
              />
              <span style={{ fontSize: '0.78rem', color: '#64748b' }}>
                The student will see this remark directly on their dashboard under their interest status.
              </span>
            </div>

            <div className="internal-modal-footer">
              <button
                onClick={() => setRemarksModalCandidate(null)}
                style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveCandidateRemarks}
                disabled={savingRemarks || !remarksText.trim()}
                className="internal-post-btn"
              >
                {savingRemarks ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
                {savingRemarks ? 'Saving...' : 'Save Remarks'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── MODAL 4: DISAPPROVE CANDIDATE (MANDATORY REMARKS) ─── */}
      {disapproveModalCandidate && (
        <div className="internal-modal-overlay">
          <div className="internal-modal-box" style={{ maxWidth: '500px' }}>
            <div className="internal-modal-header" style={{ borderBottom: '1px solid #fee2e2' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{
                  width: '36px', height: '36px', borderRadius: '10px',
                  background: '#fee2e2', color: '#dc2626',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  <XCircle size={20} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 800, color: '#991b1b' }}>
                    Disapprove Candidate
                  </h3>
                  <div style={{ fontSize: '0.78rem', color: '#64748b' }}>
                    Remarks are required to explain the disapproval
                  </div>
                </div>
              </div>
              <button
                onClick={() => { setDisapproveModalCandidate(null); setDisapproveRemarks(''); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
                title="Close"
              >
                <X size={18} color="#64748b" />
              </button>
            </div>

            <div className="internal-modal-body">
              <div style={{
                background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px',
                padding: '12px 14px', marginBottom: '16px'
              }}>
                <div style={{ fontSize: '0.9rem', color: '#1e293b', fontWeight: 700 }}>
                  {disapproveModalCandidate.student_name}
                </div>
                <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: '2px' }}>
                  USN: {disapproveModalCandidate.usn || 'N/A'} • Branch: {disapproveModalCandidate.branch || 'N/A'}
                </div>
              </div>

              <div className="internal-form-group">
                <label className="internal-form-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Disapproval Reason / Remarks <strong style={{ color: '#dc2626' }}>* (Mandatory)</strong></span>
                  <span style={{ fontSize: '0.75rem', fontWeight: 600, color: disapproveRemarks.trim() ? '#10b981' : '#dc2626' }}>
                    {disapproveRemarks.trim() ? '✓ Valid' : 'Required'}
                  </span>
                </label>
                <textarea
                  rows={4}
                  required
                  autoFocus
                  placeholder="Explain why this student is being disapproved (e.g. 'Does not meet minimum CGPA cut-off', 'Prerequisite technical skills missing', 'Failed technical screening')..."
                  value={disapproveRemarks}
                  onChange={e => setDisapproveRemarks(e.target.value)}
                  className="internal-textarea"
                  style={{
                    borderColor: !disapproveRemarks.trim() ? '#fca5a5' : '#cbd5e1',
                    background: '#ffffff'
                  }}
                />
                <span style={{ fontSize: '0.78rem', color: '#64748b', marginTop: '4px', display: 'block' }}>
                  This reason will be visible to the student under their application status.
                </span>
              </div>
            </div>

            <div className="internal-modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                type="button"
                onClick={() => { setDisapproveModalCandidate(null); setDisapproveRemarks(''); }}
                style={{
                  padding: '9px 16px', borderRadius: '8px', border: '1px solid #cbd5e1',
                  background: '#ffffff', color: '#475569', fontWeight: 600, cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDisapprove}
                disabled={savingDisapproval || !disapproveRemarks.trim()}
                style={{
                  padding: '9px 20px', borderRadius: '8px', border: 'none',
                  background: !disapproveRemarks.trim() ? '#fca5a5' : '#dc2626',
                  color: '#ffffff', fontWeight: 700, fontSize: '0.88rem',
                  cursor: !disapproveRemarks.trim() ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', gap: '6px',
                  boxShadow: disapproveRemarks.trim() ? '0 4px 12px rgba(220, 38, 38, 0.3)' : 'none'
                }}
              >
                {savingDisapproval ? <Loader2 size={15} className="animate-spin" /> : <X size={15} />}
                {savingDisapproval ? 'Disapproving...' : 'Disapprove Application'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InternalJobsModule;
